import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Music, Play, Pause, ShoppingCart, Download, Filter, Search,
  Zap, Repeat, Repeat1, Layers, LayoutGrid, Package, ChevronDown,
  Star, Clock, Headphones, Check, X, Volume2, Heart, Plus
} from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { toast } from 'sonner';
import { PageEditProvider, usePageEditMode } from '@/context/PageEditModeContext';
import { PageEditable } from '@/components/edit/PageEditable';
import { useHero } from '@/hooks/useHero';
import { PageEditToolbar } from '@/components/edit/PageEditToolbar';
import { AddSampleModal, AddCategoryModal, AddPackModal } from '@/components/sample-store/AddItemModals';
import { EditablePrice } from '@/components/sample-store/EditablePrice';
import { AdminEditorLink } from '@/components/edit/AdminEditorLink';

const API = process.env.REACT_APP_BACKEND_URL || '';

const SampleStore = () => {
  return (
    <PageEditProvider slug="sample-store">
      <SampleStoreContent />
      <PageEditToolbar />
      <AdminEditorLink to="/admin/dashboard#sample-manager-btn" label="Edit Sample Store" />
    </PageEditProvider>
  );
};

const SampleStoreContent = () => {
  const hero = useHero('sample-store');
  const [categories, setCategories] = useState([]);
  const [instruments, setInstruments] = useState([]);
  const [genres, setGenres] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedInstrument, setSelectedInstrument] = useState(null);
  const [selectedGenre, setSelectedGenre] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [playingId, setPlayingId] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const { addToCart } = useCart();
  const { getValue, isAdminUser } = usePageEditMode();
  const canAdd = isAdminUser;
  const [modal, setModal] = useState(null); // 'sample' | 'category' | 'pack' | null
  const [packs, setPacks] = useState([]);
  const searchPlaceholder = getValue('search.placeholder') || 'Search samples...';
  const allInstrumentsLabel = getValue('filters.allInstruments') || 'All Instruments';
  const allGenresLabel = getValue('filters.allGenres') || 'All Genres';

  useEffect(() => {
    fetchData();
    fetchPacks();
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [selectedCategory, selectedInstrument, selectedGenre]);

  const fetchData = async () => {
    try {
      const [catRes, instRes, genreRes] = await Promise.all([
        fetch(`${API}/api/sample-store/categories`),
        fetch(`${API}/api/sample-store/instruments`),
        fetch(`${API}/api/sample-store/genres`)
      ]);
      
      const catData = await catRes.json();
      const instData = await instRes.json();
      const genreData = await genreRes.json();
      
      setCategories(catData.categories || []);
      setInstruments(instData.instruments || []);
      setGenres(genreData.genres || []);
    } catch (err) {
      console.error('Error fetching data:', err);
    }
  };

  const fetchPacks = async () => {
    try {
      const res = await fetch(`${API}/api/sample-store/packs`);
      const data = await res.json();
      setPacks(data.packs || []);
    } catch (err) {
      console.error('Error fetching packs:', err);
    }
  };

  const deleteProduct = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      const { adminFetch } = await import('@/utils/adminAuth');
      const res = await adminFetch(`/sample-store/products/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Delete failed');
      toast.success('Sample deleted');
      fetchProducts();
    } catch (err) {
      toast.error(err.message || 'Delete failed');
    }
  };

  const deleteCategory = async (id, name) => {
    if (!window.confirm(`Delete category "${name}"? Samples in it will remain.`)) return;
    try {
      const { adminFetch } = await import('@/utils/adminAuth');
      const res = await adminFetch(`/sample-store/categories/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Delete failed');
      toast.success('Category deleted');
      fetchData();
    } catch (err) {
      toast.error(err.message || 'Delete failed');
    }
  };

  const deletePack = async (id, name) => {
    if (!window.confirm(`Delete pack "${name}"?`)) return;
    try {
      const { adminFetch } = await import('@/utils/adminAuth');
      const res = await adminFetch(`/sample-store/packs/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Delete failed');
      toast.success('Pack deleted');
      fetchPacks();
    } catch (err) {
      toast.error(err.message || 'Delete failed');
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      let url = `${API}/api/sample-store/products`;
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      if (selectedInstrument) params.append('instrument', selectedInstrument);
      if (selectedGenre) params.append('genre', selectedGenre);
      if (params.toString()) url += `?${params.toString()}`;
      
      const res = await fetch(url);
      const data = await res.json();
      setProducts(data.products || []);
    } catch (err) {
      console.error('Error fetching products:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (product) => {
    addToCart({
      id: product.id,
      name: product.name,
      price: product.sale_price || product.price,
      type: 'sample',
      image: product.image_url,
      category: product.category
    });
    toast.success(`${product.name} added to cart!`);
  };

  const getCategoryIcon = (iconName) => {
    const icons = {
      Zap: <Zap className="w-5 h-5" />,
      Repeat: <Repeat className="w-5 h-5" />,
      Repeat1: <Repeat1 className="w-5 h-5" />,
      Layers: <Layers className="w-5 h-5" />,
      LayoutGrid: <LayoutGrid className="w-5 h-5" />,
      Music: <Music className="w-5 h-5" />,
      Package: <Package className="w-5 h-5" />
    };
    return icons[iconName] || <Music className="w-5 h-5" />;
  };

  const getCategoryColor = (catId) => {
    const colors = {
      one_shot: 'from-cyan-500 to-blue-500',
      loop_2bar: 'from-green-500 to-emerald-500',
      loop_4bar: 'from-teal-500 to-cyan-500',
      loop_8bar: 'from-blue-500 to-indigo-500',
      loop_16bar: 'from-purple-500 to-violet-500',
      full_track: 'from-pink-500 to-rose-500',
      stem_pack: 'from-orange-500 to-amber-500',
      sample_pack: 'from-amber-500 to-yellow-500'
    };
    return colors[catId] || 'from-gray-500 to-gray-600';
  };

  const filteredProducts = products.filter(p => 
    !searchQuery || 
    p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.tags?.some(t => t.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const featuredProducts = products.filter(p => p.is_featured);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-amber-900/30 via-[#0a0a0a] to-purple-900/20">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-4">
              <span className="bg-gradient-to-r from-amber-400 via-orange-500 to-red-500 bg-clip-text text-transparent">
                <PageEditable path="hero.title" as="span" fallback={hero.title_top || "Brasilian Drum Samples"} />
              </span>
            </h1>
            <PageEditable
              path="hero.subtitle"
              as="p"
              multiline
              className="text-xl text-gray-400 max-w-2xl mx-auto"
              fallback="Authentic percussion samples recorded by Mestre Curtis. From one-shots to complete tracks - everything you need for Brasilian rhythm."
            />
          </motion.div>

          {/* Search Bar */}
          <div className="max-w-xl mx-auto mb-8">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                type="text"
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-[#141414] border border-[#333] rounded-full px-12 py-4 focus:border-amber-500 focus:outline-none text-lg"
              />
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`absolute right-4 top-1/2 -translate-y-1/2 p-2 rounded-full transition-colors ${showFilters ? 'bg-amber-500 text-black' : 'hover:bg-white/10'}`}
              >
                <Filter className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Quick Filters */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="max-w-4xl mx-auto mb-8 overflow-hidden"
              >
                <div className="bg-[#141414] rounded-2xl p-6 border border-[#333]">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <PageEditable path="filters.instrumentLabel" as="label" className="text-sm text-gray-400 mb-2 block" fallback="Instrument" />
                      <select
                        value={selectedInstrument || ''}
                        onChange={(e) => setSelectedInstrument(e.target.value || null)}
                        className="w-full bg-[#0a0a0a] border border-[#333] rounded-lg px-4 py-3"
                      >
                        <option value="">{allInstrumentsLabel}</option>
                        {instruments.map(inst => (
                          <option key={inst.id} value={inst.id}>{getValue(`instrument.${inst.id}.name`) || inst.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <PageEditable path="filters.genreLabel" as="label" className="text-sm text-gray-400 mb-2 block" fallback="Genre/Style" />
                      <select
                        value={selectedGenre || ''}
                        onChange={(e) => setSelectedGenre(e.target.value || null)}
                        className="w-full bg-[#0a0a0a] border border-[#333] rounded-lg px-4 py-3"
                      >
                        <option value="">{allGenresLabel}</option>
                        {genres.map(genre => (
                          <option key={genre.id} value={genre.id}>{getValue(`genre.${genre.id}.name`) || genre.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Category Cards */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <PageEditable path="browse.heading" as="h2" className="text-2xl font-bold" fallback="Browse by Type" />
          {canAdd && (
            <div className="flex items-center gap-2 flex-wrap" data-testid="sample-store-admin-actions">
              <button
                onClick={() => setModal('sample')}
                className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-600 text-black px-3 py-1.5 rounded-full text-sm font-semibold"
                data-testid="add-sample-btn"
              >
                <Plus className="w-4 h-4" /> Sample
              </button>
              <button
                onClick={() => setModal('category')}
                className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white px-3 py-1.5 rounded-full text-sm font-semibold border border-white/20"
                data-testid="add-category-btn"
              >
                <Plus className="w-4 h-4" /> Category
              </button>
              <button
                onClick={() => setModal('pack')}
                className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white px-3 py-1.5 rounded-full text-sm font-semibold border border-white/20"
                data-testid="add-pack-btn"
              >
                <Plus className="w-4 h-4" /> Pack
              </button>
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          {categories.map(cat => (
            <motion.div
              key={cat.id}
              whileHover={{ scale: 1.02, y: -2 }}
              className={`relative overflow-hidden rounded-xl p-4 text-left transition-all ${
                selectedCategory === cat.id 
                  ? 'ring-2 ring-amber-500 bg-amber-500/20' 
                  : 'bg-[#141414] hover:bg-[#1a1a1a]'
              }`}
            >
              <button
                type="button"
                onClick={() => setSelectedCategory(selectedCategory === cat.id ? null : cat.id)}
                className="absolute inset-0 w-full h-full"
                aria-label={cat.name}
              />
              <div className={`absolute inset-0 bg-gradient-to-br ${getCategoryColor(cat.id)} opacity-10 pointer-events-none`} />
              <div className="relative pointer-events-none">
                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${getCategoryColor(cat.id)} flex items-center justify-center mb-3`}>
                  {getCategoryIcon(cat.icon)}
                </div>
                <PageEditable path={`category.${cat.id}.name`} as="h3" className="font-semibold mb-1" fallback={cat.name} />
                <PageEditable path={`category.${cat.id}.description`} as="p" multiline className="text-xs text-gray-500 mb-2 line-clamp-2" fallback={cat.description} />
                <PageEditable path={`category.${cat.id}.priceRange`} as="span" className="text-sm font-bold text-amber-400" fallback={cat.price_range} />
              </div>
              {selectedCategory === cat.id && (
                <div className="absolute top-2 right-2 pointer-events-none">
                  <Check className="w-5 h-5 text-amber-500" />
                </div>
              )}
              {canAdd && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); deleteCategory(cat.id, cat.name); }}
                  className="absolute top-2 left-2 p-1 rounded-full bg-red-500/80 hover:bg-red-500 text-white z-10"
                  data-testid={`delete-category-${cat.id}`}
                  title="Delete category"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </motion.div>
          ))}
        </div>

        {/* Sample Packs */}
        {packs.length > 0 && (
          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
              <Package className="w-6 h-6 text-amber-500" />
              <PageEditable path="packs.heading" as="span" fallback="Sample Packs" />
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {packs.map(pack => (
                <div
                  key={pack.id}
                  className="relative bg-[#141414] rounded-xl p-5 border border-[#222] hover:border-amber-500/50 transition-all"
                >
                  {canAdd && (
                    <button
                      type="button"
                      onClick={() => deletePack(pack.id, pack.name)}
                      className="absolute top-2 right-2 p-1 rounded-full bg-red-500/80 hover:bg-red-500 text-white z-10"
                      data-testid={`delete-pack-${pack.id}`}
                      title="Delete pack"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <h3 className="font-semibold mb-1 pr-6">{pack.name}</h3>
                  <p className="text-xs text-gray-500 mb-3 line-clamp-2">{pack.description}</p>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      {pack.regular_price > pack.pack_price && (
                        <span className="text-gray-500 line-through text-sm mr-2">${pack.regular_price}</span>
                      )}
                      <span className="text-xl font-bold text-amber-400">${pack.pack_price}</span>
                    </div>
                    {pack.savings_percent > 0 && (
                      <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">
                        Save {pack.savings_percent}%
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mb-3">{pack.samples?.length || 0} samples included</p>
                  <button
                    onClick={() => addToCart({
                      id: pack.id,
                      name: pack.name,
                      price: pack.pack_price,
                      type: 'pack',
                      image: pack.image_url,
                    })}
                    className="w-full flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 text-black font-semibold py-2 rounded-lg transition-colors text-sm"
                  >
                    <ShoppingCart className="w-4 h-4" />
                    Add to Cart
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Featured Packs */}
        {featuredProducts.length > 0 && !selectedCategory && (
          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
              <Star className="w-6 h-6 text-amber-500" />
              <PageEditable path="featured.heading" as="span" fallback="Featured Packs" />
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {featuredProducts.map(product => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="relative bg-gradient-to-br from-amber-500/10 to-purple-500/10 rounded-2xl p-6 border border-amber-500/30"
                >
                  {canAdd && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); deleteProduct(product.id, product.name); }}
                      className="absolute top-3 right-3 z-20 p-1 rounded-full bg-red-500/80 hover:bg-red-500 text-white"
                      data-testid={`delete-featured-product-${product.id}`}
                      title="Delete sample"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <span className="px-2 py-1 bg-amber-500 text-black text-xs font-bold rounded-full">
                        <PageEditable path="featured.badge" as="span" fallback="FEATURED" />
                      </span>
                      <PageEditable path={`featuredProduct.${product.id}.name`} as="h3" className="text-xl font-bold mt-2" fallback={product.name} />
                      <PageEditable path={`featuredProduct.${product.id}.description`} as="p" multiline className="text-gray-400 text-sm mt-1" fallback={product.description} />
                    </div>
                    <div className="text-right">
                      {product.sale_price ? (
                        <>
                          <EditablePrice productId={product.id} field="price" value={product.price} canEdit={canAdd} className="text-gray-500 line-through text-sm" onUpdated={fetchProducts} />
                          <EditablePrice productId={product.id} field="sale_price" value={product.sale_price} canEdit={canAdd} className="text-2xl font-bold text-green-400 block" onUpdated={fetchProducts} />
                        </>
                      ) : (
                        <EditablePrice productId={product.id} field="price" value={product.price} canEdit={canAdd} className="text-2xl font-bold text-amber-400" onUpdated={fetchProducts} />
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => handleAddToCart(product)}
                      className="flex-1 flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 text-black font-semibold py-3 rounded-lg transition-colors"
                    >
                      <ShoppingCart className="w-5 h-5" />
                      <PageEditable path="featured.addToCart" as="span" fallback="Add to Cart" />
                    </button>
                    <button className="p-3 bg-white/10 hover:bg-white/20 rounded-lg transition-colors">
                      <Play className="w-5 h-5" />
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Products Grid */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold">
              {selectedCategory 
                ? (getValue(`category.${selectedCategory}.name`) || categories.find(c => c.id === selectedCategory)?.name || 'Samples')
                : <PageEditable path="allSamples.heading" as="span" fallback="All Samples" />
              }
            </h2>
            <span className="text-gray-500">
              {filteredProducts.length}{' '}
              <PageEditable path="allSamples.itemsLabel" as="span" fallback="items" />
            </span>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-16">
              <Music className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <PageEditable path="empty.heading" as="h3" className="text-xl font-semibold mb-2" fallback="No samples found" />
              <PageEditable path="empty.hint" as="p" multiline className="text-gray-400" fallback="Try adjusting your filters or search query" />
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {filteredProducts.filter(p => !p.is_featured).map(product => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  whileHover={{ y: -4 }}
                  className="relative bg-[#141414] rounded-xl overflow-hidden border border-[#222] hover:border-amber-500/50 transition-all group"
                >
                  {canAdd && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); deleteProduct(product.id, product.name); }}
                      className="absolute top-2 right-2 z-20 p-1 rounded-full bg-red-500/80 hover:bg-red-500 text-white"
                      data-testid={`delete-product-${product.id}`}
                      title="Delete sample"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {/* Product Image/Waveform */}
                  <div className={`h-24 bg-gradient-to-br ${getCategoryColor(product.category)} opacity-20 relative`}>
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <button 
                        onClick={() => setPlayingId(playingId === product.id ? null : product.id)}
                        className="w-12 h-12 bg-white/20 backdrop-blur rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
                      >
                        {playingId === product.id ? (
                          <Pause className="w-5 h-5" />
                        ) : (
                          <Play className="w-5 h-5 ml-1" />
                        )}
                      </button>
                    </div>
                    {product.is_new && (
                      <span className="absolute top-2 left-2 px-2 py-0.5 bg-green-500 text-black text-xs font-bold rounded">
                        <PageEditable path="product.newBadge" as="span" fallback="NEW" />
                      </span>
                    )}
                  </div>
                  
                  <div className="p-4">
                    <PageEditable path={`product.${product.id}.name`} as="h3" className="font-semibold mb-1 truncate" fallback={product.name} />
                    <PageEditable path={`product.${product.id}.description`} as="p" multiline className="text-xs text-gray-500 mb-2 line-clamp-2" fallback={product.description} />
                    
                    <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                      {product.bpm && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {product.bpm}{' '}
                          <PageEditable path="product.bpmLabel" as="span" fallback="BPM" />
                        </span>
                      )}
                      {product.duration_bars && (
                        <span>
                          {product.duration_bars}{' '}
                          <PageEditable path="product.barsLabel" as="span" fallback="bars" />
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        {product.sale_price ? (
                          <div className="flex items-center gap-2">
                            <EditablePrice productId={product.id} field="price" value={product.price} canEdit={canAdd} className="text-gray-500 line-through text-sm" onUpdated={fetchProducts} />
                            <EditablePrice productId={product.id} field="sale_price" value={product.sale_price} canEdit={canAdd} className="text-lg font-bold text-green-400" onUpdated={fetchProducts} />
                          </div>
                        ) : (
                          <EditablePrice productId={product.id} field="price" value={product.price} canEdit={canAdd} className="text-lg font-bold text-amber-400" onUpdated={fetchProducts} />
                        )}
                      </div>
                      <button
                        onClick={() => handleAddToCart(product)}
                        className="p-2 bg-amber-500 hover:bg-amber-600 text-black rounded-lg transition-colors"
                      >
                        <ShoppingCart className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* Clear Filters */}
        {(selectedCategory || selectedInstrument || selectedGenre || searchQuery) && (
          <div className="text-center py-4">
            <button
              onClick={() => {
                setSelectedCategory(null);
                setSelectedInstrument(null);
                setSelectedGenre(null);
                setSearchQuery('');
              }}
              className="text-amber-400 hover:text-amber-300 text-sm flex items-center gap-1 mx-auto"
            >
              <X className="w-4 h-4" />
              <PageEditable path="clearFilters.label" as="span" fallback="Clear all filters" />
            </button>
          </div>
        )}
      </div>

      {modal === 'sample' && (
        <AddSampleModal
          onClose={() => setModal(null)}
          onCreated={() => { fetchProducts(); fetchData(); }}
          categories={categories}
          instruments={instruments}
          genres={genres}
        />
      )}
      {modal === 'category' && (
        <AddCategoryModal
          onClose={() => setModal(null)}
          onCreated={() => fetchData()}
        />
      )}
      {modal === 'pack' && (
        <AddPackModal
          onClose={() => setModal(null)}
          onCreated={() => fetchProducts()}
          products={products}
        />
      )}
    </div>
  );
};

export default SampleStore;
